/**
 * ThemeSwitcher · v2.7.43 (2026-02-21)
 * ═════════════════════════════════════════════════════════════════════
 * Segmented 3-button toggle for the global theme (light · dark · system).
 * Lives at the very bottom of the admin sidebar. Reads/writes via the
 * shared `ThemeContext` so state stays in sync everywhere.
 */
import { Sun, Moon, Monitor } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

const OPTIONS = [
  { value: "light",  label: "Light",  Icon: Sun },
  { value: "dark",   label: "Dark",   Icon: Moon },
  { value: "system", label: "System", Icon: Monitor },
];

export default function ThemeSwitcher() {
  const { theme, setTheme } = useTheme();
  return (
    <div data-testid="theme-switcher" className="space-y-1.5">
      <div className="text-[9px] uppercase tracking-widest font-bold text-zinc-500 dark:text-zinc-400 px-1">
        Current Theme
      </div>
      <div
        role="radiogroup"
        aria-label="Theme"
        className="grid grid-cols-3 gap-1 p-1 rounded-md border border-zinc-200 dark:border-zinc-800 bg-zinc-100 dark:bg-zinc-900"
      >
        {OPTIONS.map(({ value, label, Icon }) => {
          const active = theme === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setTheme(value)}
              data-testid={`theme-${value}`}
              data-active={active ? "true" : "false"}
              className={`
                flex flex-col items-center justify-center gap-0.5 py-1.5 rounded
                text-[10px] uppercase tracking-widest font-bold
                transition-colors
                ${active
                  ? "bg-white dark:bg-zinc-950 text-amber-600 dark:text-amber-300 shadow-sm ring-1 ring-amber-500/40"
                  : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"}
              `}
              title={`Switch to ${label.toLowerCase()} mode`}
            >
              <Icon className="h-3.5 w-3.5"/>
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
